import _find from 'lodash/find';
import _indexOf from 'lodash/indexOf';
import _trim from 'lodash/trim';
import states from 'states-us';
import { getDetails } from 'use-places-autocomplete';

export const getAddressDetails = async (placeId) => {
  const parameter = {
    placeId,
    fields: ['address_components', 'geometry']
  };

  try {
    const addressDetails = await getDetails(parameter);
    return addressDetails;
  } catch (error) {
    return null;
  }
};

const LONG_FORMAT = 'long_name';
const SHORT_FORMAT = 'short_name';

const TYPE_FORMAT = {
  street_number: SHORT_FORMAT,
  route: LONG_FORMAT,
  locality: LONG_FORMAT,
  neighborhood: LONG_FORMAT,
  sublocality_level_1: LONG_FORMAT,
  sublocality: LONG_FORMAT,
  administrative_area_level_1: SHORT_FORMAT,
  administrative_area_level_2: SHORT_FORMAT,
  postal_code: SHORT_FORMAT,
  postal_code_suffix: SHORT_FORMAT,
  country: SHORT_FORMAT
};

const getFormat = (type) => {
  const format = TYPE_FORMAT[type];
  if (!format) {
    throw new Error(`Invalid type: ${type}`);
  }
  return format;
};

const getAddressByType = (addressComponents, type) => {
  const addressComponent = _find(addressComponents, (comp) => _indexOf(comp.types, type) !== -1);

  if (addressComponent) {
    return addressComponent[getFormat(type)];
  }

  return '';
};

const getStreetNumber = (address, addressComponents) => {
  const streetNum = getAddressByType(addressComponents, 'street_number');
  const route = getAddressByType(addressComponents, 'route');

  if (streetNum) {
    return streetNum;
  }

  const idx = address.indexOf(route.split(' ')[0]);
  if (idx > -1) {
    return _trim(address.substring(0, idx));
  }

  return '';
};

const getCity = (address, addressComponents) => {
  const lcAddress = address.toLowerCase();
  const addressParts = lcAddress.split(', ');
  const locality = getAddressByType(addressComponents, 'locality');
  const neighborhood = getAddressByType(addressComponents, 'neighborhood');

  if (locality && _indexOf(addressParts, locality.toLowerCase()) !== -1) {
    return locality;
  }
  if (neighborhood && _indexOf(addressParts, neighborhood.toLowerCase()) !== -1) {
    return neighborhood;
  }
  // Now, if any locality exists, return it even if it did not match exactly.
  if (locality) {
    return locality;
  }

  const sublocalityLevel1 = getAddressByType(addressComponents, 'sublocality_level_1');

  const sublocality = getAddressByType(addressComponents, 'sublocality');

  if (sublocalityLevel1) {
    return sublocalityLevel1;
  }
  if (sublocality) {
    return sublocality;
  }
  if (neighborhood) {
    return neighborhood;
  }
  return '';
};

const getCounty = (address, addressComponents) => {
  const county = getAddressByType(addressComponents, 'administrative_area_level_2');

  if (county) {
    return county;
  }

  const state = getAddressByType(addressComponents, 'administrative_area_level_1');
  const city = getCity(address, addressComponents);

  if (state === 'VA') {
    return `${city} (city)`;
  }
  return '';
};

const getCountry = (addressComponents) => {
  const country = getAddressByType(addressComponents, 'country');

  if (country) {
    return country;
  }

  return '';
};

const getZip = (addressComponents) => {
  const zip = getAddressByType(addressComponents, 'postal_code');
  const zipSuffix = getAddressByType(addressComponents, 'postal_code_suffix');
  const zipCode = zipSuffix ? [zip, zipSuffix].join('-') : zip;
  return zipCode;
};

const getAddressLine1 = (address, addressComponents) => {
  const route = getAddressByType(addressComponents, 'route');
  const streetNumber = getStreetNumber(address, addressComponents);
  const addressLine1 = streetNumber ? _trim([streetNumber, route].join(' ')) : route;

  return addressLine1;
};

const buildAddress = async (address, placeId, otherFields) => {
  const addressDetails = await getAddressDetails(placeId);
  if (!addressDetails) {
    return null;
  }
  const { address_components: addressComponents, geometry } = addressDetails;

  const addressLine1 = getAddressLine1(address, addressComponents);
  const city = getCity(address, addressComponents);
  let stateAbbreviation = getAddressByType(addressComponents, 'administrative_area_level_1');

  // If the google maps API returns the full state name, convert it to the abbreviation.
  const state = states.find((st) => st.name === stateAbbreviation);
  if (state) {
    stateAbbreviation = state.abbreviation;
  }

  const county = getCounty(address, addressComponents);
  const country = getCountry(addressComponents);
  const zip = getZip(addressComponents);
  const lat = geometry?.location?.lat();
  const lng = geometry?.location?.lng();

  return {
    addressLine1,
    addressLine2: otherFields?.addressLine2 || '',
    city,
    county,
    state: stateAbbreviation,
    country,
    zip,
    lat,
    lng
  };
};

export { buildAddress };
